import { entityTitle, refAccepts, type FieldDef, type FieldDefType, type FieldValue, type StoryNode, type TimeUnit } from '@dispositio/core';
import { Fragment, useId, type ReactNode } from 'react';
import { de } from '../../i18n/de';
import { useDraft } from '../../lib/useDraft';
import { useStoryEnv } from '../../state/storyEnv';
import { DateTimeInput } from './DateTimeInput';
import { Field } from '../Field';
import { ImageField } from './ImageField';
import { RichTextEditor } from '../editor/RichTextEditor';

interface DraftProps {
  value: string;
  /** Datum/Uhrzeit: `false` heißt abgelehnt (das Feld zeigt wieder den gespeicherten Wert) */
  onSave: (v: string) => boolean | void;
  label: string;
  multiline?: boolean;
  type?: 'text' | 'date' | 'datetime-local';
  testId?: string;
  placeholder?: string;
  subtitle?: string;
  /** Datum/Uhrzeit: auch eine eigene Zählung annehmen (siehe DateTimeInput) */
  unit?: TimeUnit | 'any';
}

/** Textfeld mit Autosave: speichert nach kurzer Pause und sofort beim Verlassen. */
export function DraftField({ value, onSave, label, multiline, type = 'text', testId, placeholder, subtitle, unit }: DraftProps) {
  const d = useDraft(value, onSave, 700);
  const fieldId = useId();
  // Datum und Uhrzeit: eigenes Feld im 24-Stunden-Format (übernimmt beim Verlassen bzw. mit Enter)
  if (type === 'datetime-local' || type === 'date') {
    return (
      <Field label={label} subtitle={subtitle} htmlFor={fieldId}>
        <DateTimeInput id={fieldId} value={value} onCommit={onSave} unit={unit} testId={testId} />
      </Field>
    );
  }
  return (
    <Field label={label} subtitle={subtitle}>
      {multiline ? (
        <textarea value={d.draft} onChange={(e) => d.change(e.target.value)} onBlur={d.flush} data-testid={testId} rows={4} placeholder={placeholder} />
      ) : (
        <input type={type} value={d.draft} onChange={(e) => d.change(e.target.value)} onBlur={d.flush} data-testid={testId} placeholder={placeholder} />
      )}
    </Field>
  );
}

/** Mehrzeiliges Textfeld mit Autosave und Auswahl für @: Verweise auf Einträge gehen auch in Beschreibungen. */
/**
 * Mehrzeiliges Textfeld mit Autosave und @-Auswahl. „Sarah @sarah_baier“ verknüpft das Wort Sarah mit dem Eintrag (blau);
 * gespeichert wird reiner Text mit `Wort[@schlüssel]`.
 */
function MentionField({ value, onSave, label, testId, placeholder, subtitle, node }: Omit<DraftProps, 'multiline' | 'type' | 'unit'> & { node?: StoryNode }) {
  const d = useDraft(value, onSave, 700);
  return (
    <Field label={label} subtitle={subtitle}>
      <RichTextEditor variant="field" value={d.draft} onChange={d.change} onSave={d.flush} onBlur={d.flush} node={node} placeholder={placeholder ?? ''} ariaLabel={label} testId={testId} />
    </Field>
  );
}

/**
 * Feste Auswahl als Leiste aus Umschaltknöpfen: höchstens einer ist gewählt, ein Klick auf den gewählten hebt die Wahl wieder auf.
 * Ohne Wahl wird null gespeichert.
 */
function ChoiceField({ label, options, value, onChange, testId }: { label: string; options: string[]; value: string | null; onChange: (v: string | null) => void; testId: string }) {
  return (
    <Field label={label}>
      <div className="seg choice" role="radiogroup" aria-label={label} data-testid={testId}>
        {options.map((o) => (
          <button
            key={o}
            type="button"
            role="radio"
            aria-checked={value === o}
            className={`seg-btn${value === o ? ' active' : ''}`}
            onClick={() => onChange(value === o ? null : o)}
            data-testid={`${testId}-${o}`}
          >
            {o}
          </button>
        ))}
      </div>
    </Field>
  );
}

/** Verweisfeld: Auswahl eines Eintrags der verlangten Klasse (z. B. die Perspektive einer Szene); leer = keine Angabe. */
function RefField({ label, refType, value, onChange, testId, subtitle }: { label: string; refType?: string; value: string | null; onChange: (v: string | null) => void; testId: string; subtitle?: string }) {
  const { ctx } = useStoryEnv();
  const options = ctx.story.entities
    .filter((e) => refAccepts(ctx, refType, e.typeId))
    .map((e) => ({ id: e.id, title: entityTitle(ctx, e.id) }))
    .sort((a, b) => a.title.localeCompare(b.title, 'de'));
  return (
    <Field label={label} subtitle={subtitle}>
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} data-testid={testId}>
        <option value="">{de.details.refNone}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.title}
          </option>
        ))}
      </select>
    </Field>
  );
}

/** Zahlenfeld: leer = kein Wert. */
export function NumberField({ value, onSave, label, testId, className }: { value: number | null; onSave: (v: number | null) => void; label: string; testId?: string; className?: string }) {
  const d = useDraft(value === null ? '' : String(value), (v) => onSave(v.trim() === '' || !Number.isFinite(Number(v.replace(',', '.'))) ? null : Number(v.replace(',', '.'))), 700);
  return (
    <Field label={label} className={className}>
      <input type="text" inputMode="decimal" value={d.draft} onChange={(e) => d.change(e.target.value)} onBlur={d.flush} data-testid={testId} />
    </Field>
  );
}

interface FormProps {
  /** Node, zu dem die Felder gehören (Einträge seiner Szene stehen bei @ oben) */
  node?: StoryNode;
  defs: FieldDef[];
  values: Record<string, FieldValue>;
  onChange: (key: string, value: FieldValue) => void;
  idPrefix?: string;
  /** Bedienelemente rechts an jedem Feld (z. B. Menü zum Umbenennen und Entfernen des Feldes) */
  fieldActions?: (def: FieldDef) => ReactNode;
  /** Titelbild der Klasse (`imageKey`): steht als erstes Feld ganz oben */
  imageKey?: string | null;
}

/** Formular für die statischen Felder eines Typs; die Eingabeart richtet sich nach dem Feldtyp. */
export function FieldsForm({ defs, values, onChange, idPrefix = 'f', node, fieldActions, imageKey }: FormProps) {
  const titleImage = imageKey ? defs.find((d) => d.key === imageKey && d.type === 'image') : undefined;
  const ordered = titleImage ? [titleImage, ...defs.filter((d) => d !== titleImage)] : defs;
  const one = (def: FieldDef): ReactNode => {
    const v = values[def.key] ?? null;
    const testId = `${idPrefix}-${def.key}`;
    switch (def.type as FieldDefType) {
      case 'longtext':
        return <MentionField label={def.label} value={v === null ? '' : String(v)} onSave={(s) => onChange(def.key, s)} testId={testId} subtitle={def.help} placeholder={def.placeholder} node={node} />;
      case 'number':
        return <NumberField label={def.label} value={typeof v === 'number' ? v : v === null ? null : Number(v)} onSave={(n) => onChange(def.key, n)} testId={testId} />;
      case 'date':
        return <DraftField label={def.label} type="datetime-local" unit="any" value={v === null ? '' : String(v)} onSave={(s) => onChange(def.key, s)} testId={testId} />;
      case 'image':
        return <ImageField label={def.label} value={typeof v === 'string' ? v : null} onChange={(id) => onChange(def.key, id)} />;
      case 'ref':
        return <RefField label={def.label} refType={def.refType} value={typeof v === 'string' ? v : null} onChange={(id) => onChange(def.key, id)} testId={testId} subtitle={def.help} />;
      default:
        if (def.options) return <ChoiceField label={def.label} options={def.options} value={typeof v === 'string' ? v : null} onChange={(c) => onChange(def.key, c)} testId={testId} />;
        return <DraftField label={def.label} value={v === null ? '' : String(v)} onSave={(s) => onChange(def.key, s)} testId={testId} subtitle={def.help} placeholder={def.placeholder} />;
    }
  };
  return (
    <div className="col">
      {ordered.map((def) =>
        fieldActions ? (
          <div key={def.key} className="field-with-actions">
            {one(def)}
            <div className="field-actions">{fieldActions(def)}</div>
          </div>
        ) : (
          <Fragment key={def.key}>{one(def)}</Fragment>
        ),
      )}
    </div>
  );
}
